import { useContext } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';
import api from 'Api';
import { DYNAMIC_PRICE_POLLING_INTERVAL } from 'Helpers/assets';
import { EnvContext } from 'States/env/envState';
import useHandleError from 'Utils/handleError';
import { i18nextKeys } from 'Lang/i18nextKeys';
import i18nextTranslate from 'Lang/i18nextTranslate';

const PURCHASE_METHODS_QUERY_KEY = 'purchaseMethodsQuery';
const PURCHASE_METHODS_PAGINATED_QUERY_KEY = 'purchaseMethodsPaginatedQuery';

const usePurchaseMethodsQuery = ({
  queryKeys = [],
  queryFnArgs = [],
  onSuccess = () => {},
  onError = () => {},
  enablePolling = false,
  ...config
}) => {
  const { env } = useContext(EnvContext);
  const handleError = useHandleError();
  return useQuery({
    queryKey: [PURCHASE_METHODS_QUERY_KEY, ...queryKeys],
    queryFn: () => api.Assets.getAssetsConfig({ tenantId: env?.TenantId }),
    select: ({ value }) => value,
    onError: (error) => {
      onError(error);
      handleError({ error });
    },
    refetchInterval: enablePolling ? DYNAMIC_PRICE_POLLING_INTERVAL : false,
    ...config,
  });
};

const FEATURED_PURCHASE_METHODS_QUERY_KEY = 'featuredPurchaseMethodsQuery';

const useFeaturedPurchaseMethodsQuery = ({
  onError = () => {},
  ...config
}) => {
  const { env } = useContext(EnvContext);
  const handleError = useHandleError();
  return useQuery({
    queryKey: [FEATURED_PURCHASE_METHODS_QUERY_KEY],
    queryFn: () => api.Assets.getAssetsConfig({
      tenantId: env?.TenantId,
      filter: "IsFeaturedAsset"
    }),
    select: ({ value }) => value,
    onError: (error) => {
      onError(error);
      const message = i18nextTranslate(i18nextKeys.errorPurchaseAssetLoadingError);
      handleError({ error, message });
    },
    staleTime: 300000,
    ...config,
  });
};

const usePurchaseMethodPollingQuery = ({
  queryKeys = [],
  queryFnArgs = [],
  onSuccess = () => {},
  onError = () => {},
  id,
  ...config
}) => {
  const { env } = useContext(EnvContext);
  const handleError = useHandleError();
  return useQuery({
    queryKey: [id, ...queryKeys],
    queryFn: () => api.Assets.getAssetConfig(env?.TenantId, id),
    select: ({ Price }) => Price,
    onError: (error) => {
      onError(error);
      handleError({ error });
    },
    refetchInterval: DYNAMIC_PRICE_POLLING_INTERVAL,
    ...config,
  });
};

const usePurchaseMethodsPaginatedQuery = ({
  onError = () => {},
  pageSize = 8,
  errorMessage
}) => {
  const { env } = useContext(EnvContext);
  const handleError = useHandleError();
  return useInfiniteQuery({
    queryKey: [PURCHASE_METHODS_PAGINATED_QUERY_KEY, pageSize],
    queryFn: async ({ pageParam: skip = 0 }) => {
      const {
        '@odata.count': totalCount,
        value: assetsConfig
      } = await api.Assets.getAssetsConfig({
        tenantId: env?.TenantId,
        top: pageSize,
        skip
      });
      return { assetsConfig, totalCount, currentSkip: skip }
    },
    getNextPageParam: (lastPage) => {
      const totalLoaded = lastPage.currentSkip + lastPage.assetsConfig.length;
      return totalLoaded < lastPage.totalCount ? totalLoaded : undefined;
    },
    onError: (error) => {
      onError(error);
      handleError({ error, message: errorMessage });
    },
    keepPreviousData: true,
    staleTime: 600000
  });
};

const usePurchaseMethods = {
  purchaseMethodsQuery: usePurchaseMethodsQuery,
  featuredMethodsQuery: useFeaturedPurchaseMethodsQuery,
  purchaseMethodsPaginatedQuery: usePurchaseMethodsPaginatedQuery,
  methodPollingQuery: usePurchaseMethodPollingQuery
};

export default usePurchaseMethods;
